import { useState, useRef, useEffect } from 'react'
import {
  Box,
  TextField,
  IconButton,
  InputAdornment,
  Popover,
  CircularProgress,
} from '@mui/material'
import { Send, EmojiEmotions, Image as ImageIcon, Close } from '@mui/icons-material'

interface MessageInputProps {
  onSend: (content: string, imageId?: string) => void
  onTyping?: (isTyping: boolean) => void
  onImageUpload?: (file: File) => Promise<string | undefined>
  disabled?: boolean
  placeholder?: string
}

// Common emoji set
const EMOJI_LIST = [
  '😊', '😂', '😍', '🥰', '😎', '🤔', '😅', '😢',
  '👍', '👎', '❤️', '🎉', '🔥', '⭐', '✅', '🙏',
  '💪', '🎯', '🏆', '🌟', '💯', '🚀', '⛺', '🥾',
  '🏕️', '🌲', '🌄', '🗺️', '🧭', '🎒', '⛰️', '🌅',
]

export function MessageInput({
  onSend,
  onTyping,
  onImageUpload,
  disabled = false,
  placeholder = 'Type a message...',
}: MessageInputProps) {
  const [message, setMessage] = useState('')
  const [emojiAnchor, setEmojiAnchor] = useState<null | HTMLElement>(null)
  const [imagePreview, setImagePreview] = useState<string | null>(null)
  const [imageFile, setImageFile] = useState<File | null>(null)
  const [isUploading, setIsUploading] = useState(false)
  const inputRef = useRef<HTMLInputElement>(null)
  const fileInputRef = useRef<HTMLInputElement>(null)
  const typingTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null)

  // Clear typing timeout on unmount
  useEffect(() => {
    return () => {
      if (typingTimeoutRef.current) {
        clearTimeout(typingTimeoutRef.current)
      }
    }
  }, [])

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setMessage(e.target.value)

    // Handle typing indicator
    if (onTyping) {
      onTyping(true)

      // Clear existing timeout
      if (typingTimeoutRef.current) {
        clearTimeout(typingTimeoutRef.current)
      }

      // Set new timeout to stop typing indicator
      typingTimeoutRef.current = setTimeout(() => {
        onTyping(false)
      }, 2000)
    }
  }

  const handleSend = async () => {
    const trimmedMessage = message.trim()
    if ((!trimmedMessage && !imageFile) || disabled || isUploading) return

    let imageId: string | undefined

    // Upload image if present
    if (imageFile && onImageUpload) {
      setIsUploading(true)
      try {
        imageId = await onImageUpload(imageFile)
      } catch (error) {
        console.error('Failed to upload image:', error)
        setIsUploading(false)
        return
      }
      setIsUploading(false)
    }

    onSend(trimmedMessage || '', imageId)
    setMessage('')
    setImagePreview(null)
    setImageFile(null)

    // Stop typing indicator
    if (onTyping) {
      onTyping(false)
      if (typingTimeoutRef.current) {
        clearTimeout(typingTimeoutRef.current)
      }
    }

    // Focus back on input
    inputRef.current?.focus()
  }

  const handleImageSelect = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0]
    if (!file) return

    // Validate file type
    if (!file.type.startsWith('image/')) {
      return
    }

    // Create preview
    const reader = new FileReader()
    reader.onload = () => {
      setImagePreview(reader.result as string)
    }
    reader.readAsDataURL(file)
    setImageFile(file)

    // Reset file input
    if (fileInputRef.current) {
      fileInputRef.current.value = ''
    }
  }

  const handleRemoveImage = () => {
    setImagePreview(null)
    setImageFile(null)
  }

  const handleKeyPress = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault()
      handleSend()
    }
  }

  const handleEmojiClick = (emoji: string) => {
    setMessage((prev) => prev + emoji)
    setEmojiAnchor(null)
    inputRef.current?.focus()
  }

  return (
    <Box
      sx={{
        p: 2,
        backgroundColor: 'background.paper',
        borderTop: 1,
        borderColor: 'divider',
      }}
    >
      {/* Image Preview */}
      {imagePreview && (
        <Box sx={{ mb: 1, position: 'relative', display: 'inline-block' }}>
          <img
            src={imagePreview}
            alt="Preview"
            style={{
              maxWidth: '200px',
              maxHeight: '150px',
              borderRadius: '8px',
              objectFit: 'cover',
            }}
          />
          <IconButton
            size="small"
            onClick={handleRemoveImage}
            sx={{
              position: 'absolute',
              top: -8,
              right: -8,
              backgroundColor: 'background.paper',
              boxShadow: 1,
              '&:hover': { backgroundColor: 'error.light', color: 'white' },
            }}
          >
            <Close fontSize="small" />
          </IconButton>
        </Box>
      )}

      {/* Hidden file input */}
      <input
        type="file"
        ref={fileInputRef}
        onChange={handleImageSelect}
        accept="image/*"
        style={{ display: 'none' }}
      />

      <TextField
        fullWidth
        multiline
        maxRows={4}
        value={message}
        onChange={handleChange}
        onKeyPress={handleKeyPress}
        placeholder={placeholder}
        disabled={disabled || isUploading}
        inputRef={inputRef}
        InputProps={{
          sx: {
            borderRadius: 3,
            bgcolor: 'action.hover',
          },
          startAdornment: (
            <InputAdornment position="start">
              <IconButton
                size="small"
                onClick={(e) => setEmojiAnchor(e.currentTarget)}
                disabled={disabled || isUploading}
              >
                <EmojiEmotions fontSize="small" />
              </IconButton>
              {onImageUpload && (
                <IconButton
                  size="small"
                  onClick={() => fileInputRef.current?.click()}
                  disabled={disabled || isUploading}
                >
                  <ImageIcon fontSize="small" />
                </IconButton>
              )}
            </InputAdornment>
          ),
          endAdornment: (
            <InputAdornment position="end">
              {isUploading ? (
                <CircularProgress size={24} />
              ) : (
                <IconButton
                  onClick={handleSend}
                  disabled={(!message.trim() && !imageFile) || disabled}
                  color="primary"
                >
                  <Send />
                </IconButton>
              )}
            </InputAdornment>
          ),
        }}
      />

      {/* Emoji Picker Popover */}
      <Popover
        open={Boolean(emojiAnchor)}
        anchorEl={emojiAnchor}
        onClose={() => setEmojiAnchor(null)}
        anchorOrigin={{
          vertical: 'top',
          horizontal: 'left',
        }}
        transformOrigin={{
          vertical: 'bottom',
          horizontal: 'left',
        }}
      >
        <Box
          sx={{
            p: 1,
            display: 'grid',
            gridTemplateColumns: 'repeat(8, 1fr)',
            gap: 0.5,
            maxWidth: 280,
          }}
        >
          {EMOJI_LIST.map((emoji) => (
            <IconButton
              key={emoji}
              size="small"
              onClick={() => handleEmojiClick(emoji)}
              sx={{
                fontSize: '1.2rem',
                '&:hover': {
                  backgroundColor: 'primary.light',
                },
              }}
            >
              {emoji}
            </IconButton>
          ))}
        </Box>
      </Popover>
    </Box>
  )
}
